<template>
  <div class="study-request-index">
    <vt-side-panel
      v-if="showSidePanel"
      :visible="showSidePanel"
    >
      <template v-if="!isPatient">
        <study-request-detail
          v-if="selectedStudyRequest"
          :tab-index="tabIndex"
          :study-request="selectedStudyRequest"
          :cas-id="reqCasId"
          :show-case-report="true"
          :tracking-resource="requestTrackingResource"
          class="study-request-buttons"
          @loadSummary="$emit('loadSummary')"
          @onDeleteAttachment="gridApi.setServerSideDatasource(studyRequestDatasource)"
          @tabIndex="tabIndex = $event"
        >
          <b-button
            v-if="hasLabOperationGlobalMixin(selectedStudyRequest.laboratory.labId, requestOperation.CREATE)"
            id="createRequest"
            slot="buttons-end"
            v-tooltip="$t('hoverBtn.createRequest')"
            class="btn__icon btn__icon--primary px-2 py-0"
            variant="primary"
            @click="createRequest(selectedStudyRequest.patient.id)"
          >
            <i
              class="far fa-plus"
              aria-hidden="true"
            />
          </b-button>
          <b-button
            v-if="hasLabOperationGlobalMixin(selectedStudyRequest.laboratory.labId, requestOperation.CREATE)"
            id="editRequest"
            slot="buttons-end"
            class="btn__icon btn__icon--primary px-2 py-0"
            :disabled="selectedStudyRequest.status.code !== REQUEST_STATES.DRAFT.code"
            variant="primary"
            @click="editRequest(selectedStudyRequest.laboratory.labId, selectedStudyRequest.id)"
          >
            <i
              v-tooltip="$t('hoverBtn.editRequest')"
              class="far fa-pen"
              aria-hidden="true"
            />
          </b-button>

          <b-button
            v-if="selectedStudyRequest.status.code === REQUEST_STATES.REQUESTED.code && hasLabOperationGlobalMixin(selectedStudyRequest.laboratory.labId, requestOperation.PETITIONER_CANCEL)"
            id="cancelRequest"
            slot="buttons-end"
            class="btn__icon btn__icon--primary px-2 py-0"
            :disabled="htmlDisabledSelectedRequestCancelButton"
            variant="primary"
            @click="cancelRequestConfirmation([selectedStudyRequest])"
          >
            <i
              v-tooltip="$t('hoverBtn.cancelRequest')"
              class="far fa-ban"
              aria-hidden="true"
            />
          </b-button>

          <b-button
            v-if="hasLabOperationGlobalMixin(selectedStudyRequest.laboratory.labId, requestOperation.CREATE)"
            id="deleteRequest"
            slot="buttons-end"
            class="btn__icon btn__icon--primary px-2 py-0"
            :disabled="htmlDisabledSelectedRequestDeleteButton"
            variant="primary"
            @click="deleteDraftRequestConfirmation([selectedStudyRequest])"
          >
            <i
              v-tooltip="$t('component.studyRequest.form.discardDraft')"
              class="far fa-trash-alt"
              aria-hidden="true"
            />
          </b-button>

          <b-button
            v-if="hideBtn"
            id="attachToRequest"
            slot="buttons-end"
            class="btn__icon btn__icon--primary px-2 py-0"
            :disabled="![REQUEST_STATES.DRAFT.code, REQUEST_STATES.REQUESTED.code].includes(selectedStudyRequest.status.code)"
            variant="primary"
            @click="showAddFileModal(selectedStudyRequest)"
          >
            <i
              v-tooltip="$t('hoverBtn.attach')"
              class="far fa-paperclip"
              aria-hidden="true"
            />
          </b-button>

          <b-button
            v-if="printEnabled
              && hasLabOperationGlobalMixin(selectedStudyRequest.laboratory.labId, requestOperation.PRINT_REQUEST_AND_LABELS)"
            id="requestFormRequest"
            slot="buttons-end"
            class="btn__icon btn__icon--primary px-2 py-0"
            variant="primary"
            @click="printRequestReport(selectedStudyRequest)"
          >
            <i
              v-tooltip="$t('clinical.btn.printRequest')"
              class="far fa-print"
              aria-hidden="true"
            />
          </b-button>

          <b-button
            v-if="printEnabled
              && hasLabOperationGlobalMixin(selectedStudyRequest.laboratory.labId,requestOperation.PRINT_REQUEST_AND_LABELS)"
            id="requestFormSubRequestPrintLabels"
            slot="buttons-end"
            v-tooltip="$t('hoverBtn.printLabels')"
            class="btn__icon btn__icon--primary px-2 py-0"
            variant="primary"
            @click="printSubrequestLabels(selectedStudyRequest)"
          >
            <i
              class="far fa-ticket-alt"
              aria-hidden="true"
            />
          </b-button>

          <b-button
            v-if="hasLabOperationGlobalMixin(selectedStudyRequest.laboratory.labId, requestOperation.PRINT_REQUEST_AND_LABELS)"
            id="requestFormSubRequestPrintLabelPopover"
            slot="buttons-end"
            v-tooltip="$t('component.studyRequest.subRequestForm.printCustomLabels')"
            class="btn__icon btn__icon--primary px-2 py-0"
            variant="primary"
          >
            <i
              class="icon-ticket-setup"
              aria-hidden="true"
            />
          </b-button>

          <vt-loading-button
            v-if="printEnabled
              && hasLabOperationGlobalMixin(selectedStudyRequest.laboratory.labId, requestOperation.PRINT_REQUEST_AND_LABELS)"
            id="requestFormDownloadRequest"
            slot="buttons-end"
            v-tooltip="$t('clinical.grid.viewRequestReport')"
            class="btn__icon btn__icon--primary px-2 py-0"
            variant="primary"
            :params="{ promise: downloadRequestReportPdf }"
          >
            <i
              class="far fa-file-medical"
              aria-hidden="true"
            />
          </vt-loading-button>

          <vt-loading-button
            v-if="selectedStudyRequest.status.code === REQUEST_STATES.COMPLETED.code && hasLabOperationGlobalMixin(selectedStudyRequest.laboratory.labId, caseOperation.PETITIONER_PRINT)"
            id="requestFormDownloadCase"
            slot="buttons-end"
            v-tooltip="$t('clinical.btn.viewReport')"
            class="btn__icon btn__icon--primary px-2 py-0"
            variant="primary"
            :params="{ promise: downloadCaseAPReportPdf }"
          >
            <i
              class="far fa-file-contract"
              aria-hidden="true"
            />
          </vt-loading-button>

          <b-button
            v-if="hideBtn"
            id="requestDetailSpecialMenu"
            slot="buttons-end"
            class="btn-main ellipsis"
            variant="primary"
          >
            <i
              class="fal fa-ellipsis-v"
              aria-hidden="true"
            />
          </b-button>

          <b-button
            id="requestsSidePanelClose"
            slot="buttons-end"
            v-shortkey="['shift', 'esc']"
            class="btn__icon btn__icon--primary pl-3 pr-0 py-0"
            @shortkey.prevent="showSidePanel = false"
            @click="showSidePanel = false"
          >
            <i
              class="fal fa-times"
              aria-hidden="true"
            />
          </b-button>

          <print-label-popover
            slot="buttons-end"
            target="requestFormSubRequestPrintLabelPopover"
            label-field="surExternalId"
            :elements="selectedStudyRequest.subrequests"
            :lab-id="selectedStudyRequest.laboratory.labId"
            :parent-id="selectedStudyRequest.id"
            @printSelectedLabelsWithCopies="printSelectedSubrequestLabelsWithCopies"
          />
        </study-request-detail>
      </template>
      <template v-else>
        <b-row class="px-4 d-flex align-items-center mb-2">
          <b-col class="d-flex align-items-center">
            <span class="main"><i
              class="far fa-user-circle mr-2"
              aria-hidden="true"
            />{{ $t('global.infoPatient') }}</span>
          </b-col>
          <b-col
            cols="auto"
            class="d-flex justify-content-end"
          >
            <div>
              <b-button
                v-shortkey="['shift', 'esc']"
                class="btn__icon btn__icon--primary p-0"
                @shortkey.prevent="showSidePanel = false"
                @click="showSidePanel = false"
              >
                <i
                  class="far fa-times"
                  aria-hidden="true"
                />
              </b-button>
            </div>
          </b-col>
        </b-row>
        <div
          v-if="selectedStudyRequest"
          class="content-scroll"
        >
          <b-row class="px-4">
            <b-col class="mt-3">
              <patient-info :patient="selectedStudyRequest.patient" />
            </b-col>
          </b-row>
        </div>
      </template>
    </vt-side-panel>

    <b-row class="search-block mb-3 mt-1">
      <b-col
        xl="8"
        lg="10"
        md="10"
        class="d-flex"
      >
        <local-searcher-clinical
          :identifier-options="identifierScopesForSearch"
          :initial-identifier="identifierScopesForSearch[0]"
          @requestSearch="requestSearch"
          @studySearch="studySearch"
          @patientSearch="patientSearch"
        />
      </b-col>
      <b-col
        xl="4"
        lg="2"
        md="2"
        class="text-right"
      >
        <div
          class="mt-1"
          style="margin-bottom: -1px;"
        >
          <b-button
            v-if="hasOperationInAnyLaboratoryGlobalMixin(requestOperation.CREATE)"
            v-shortkey="['alt', 'n']"
            qa="clinical-grid-create-request-button"
            class="ml-3 btn btn--primary btn-secondary btn-sm"
            @shortkey.prevent="$refs.clinicalSearchModal.show()"
            @click="$refs.clinicalSearchModal.show()"
          >
            <fa-icon
              class="mr-1"
              :icon="['fal', 'plus']"
            />
            {{ $tc('global.request',1) }}
          </b-button>
        </div>
      </b-col>
    </b-row>
    <div
      v-if="!isPatient"
      class="grid-container"
    >
      <GridHeader
        :gridtitle="sideBarState.selectedOption !== undefined ? $t(sideBarState.selectedOption.label) : ''"
        :gridsubtitle="msgSelectedRows"
        :qa="componentId"
      >
        <grid-display-options
          v-if="gridApi"
          :ui-component="uiComponentGrid"
          @changedPageSize="changedPageSize($event)"
          @changedMode="changedMode($event)"
        />
        <div class="pl-1 pr-0 d-flex align-items-center">
          <button
            id="samplesCleanFilters"
            v-tooltip="$t('global.cleanFilter')"
            v-shortkey="['ctrl', 'alt', 'f']"
            qa="clinical-grid-clean-button"
            type="button"
            class="btn btn__icon btn__icon--primary mx-2 p-0 btn-secondary"
            @shortkey.prevent="cleanFilters"
            @click="cleanFilters"
          >
            <i
              class="far fa-filter"
              aria-hidden="true"
            />
          </button>
        </div>

        <vt-report-export-buttons
          source="clinical-grid"
          :export-report="exportReport"
        />
      </GridHeader>
      <vt-grid
        class="study-request-grid"
        qa="clinical-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-id="componentId"
        :component-code="uiComponentGrid"
      />
      <div
        v-if="busy"
        class="spinner-wrapper"
      >
        <vt-loading />
      </div>
    </div>
    <modal-confirmation
      id="confirmationModalCheckableClinical"
      ref="confirmationModal"
      :content="modalConfirmationMessage"
      :store-name="storeNameModal"
      :ui-component="uiComponentModal"
      :name="nameModal"
    />
    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :request-created="true"
        :attachment-entity-type="ATTACHMENT_ENTITY_TYPE"
        :entities-id-array="entitiesIdArray"
        @attachmentsAdded="onAttachmentsAdded"
      />
    </modal-wrapper>
    <add-custom-filter-modal
      id="addFilterModal"
      ref="addFilterModal"
      @ok="onAddFilterOk"
    />

    <print-component ref="printComponent" />
    <modal-wrapper ref="clinicalSearchModal">
      <clinical-search-patient-modal
        id-modal="clinicalSearchModal"
        @patientSelectedCreateRequest="createRequest($event)"
        @patientSelected="createRequest($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ModalWrapper from '@/components/ModalWrapper'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import AddFileModal from '@/components/AddFileModal'
import AddCustomFilterModal from '@/components/customFilter/AddCustomFilterModal'
import StudyRequestDetail from '@/components/common/detail/CommonRequestDetail'
import PrintLabelPopover from '@/modules/clinicalModule/components/Popover/PrintLabelPopover'
import VtGrid from '@/components/VtGrid/VtGrid'
import GridHeader from '@/components/GridHeader'
import clinicalRequestService from '@/modules/clinicalModule/services/clinicalRequestService'
import clinicalRequestPrintService from '@/modules/printModule/services/ClinicalRequestPrintService'
import clinicalCasePrintService from '@/modules/printModule/services/ClinicalCasePrintService'
import customFilterResource from '@/api/resources/ui/CustomFilterResource'
import { ClinicalDatasource } from '@/modules/clinicalModule/views/grid/clinical-datasource'
import { clinicalGridColumnDefs } from '@/modules/clinicalModule/views/grid/clinical-grid.column-defs'
import { AgGridCallbackEventType } from '@/shared/models/ag-grid.model'
import sideBarStore from '@/modules/clinicalModule/components/SideBar/sideBarStore'
import sessionStore from '@/session/sessionStore'
import { isEmpty } from 'lodash'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import LocalSearcherClinical from '@/components/SearcherModuleEntitySelection'
import ClinicalSearchPatientModal from '@/modules/clinicalModule/components/clinical-search-patient-modal/ClinicalSearchPatientModal'
import { REQUEST_STATES } from '@/api/resources/entityState/request-state'
import { RequestOperation } from '@/api/operations/models/request-operation'
import { CaseOperation } from '@/api/operations/models/case-operation'
import ModalConfirmation from '@/components/modals/ModalConfirmationCheckable'
import commonRequestService from '@/api/resources/common/commonRequestService'
import requestTrackingResource from '@/api/resources/RequestTrackingResource'
import { IDENTIFIER_SCOPES, PATIENT_IDENTIFIER } from '@/api/resources/identifierScopesResource'
import GridDisplayOptions from '@/components/GridDisplayOptions'
import PatientInfo from '@/components/searcherPatientModal/PatientInfo'
import PatientResource from '@/api/resources/PatientResource'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import clinicalGridContextMenuMixin from '@/modules/clinicalModule/mixins/clinicalGridContextMenuMixin'
import { GridExportMixin } from '@/mixins/grid-export.mixin'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { GridUtils } from '@/shared/util/grid-utils'
import { HelperUtils } from '@/shared/util/helper-utils'

export default {
    name: 'ClinicalGrid',
    components: {
        GridDisplayOptions,
        LocalSearcherClinical,
        VtGrid,
        GridHeader,
        StudyRequestDetail,
        AddFileModal,
        AddCustomFilterModal,
        ModalConfirmation,
        PrintLabelPopover,
        ClinicalSearchPatientModal,
        ModalWrapper,
        PatientInfo
    },
    mixins: [
        clinicalGridContextMenuMixin,
        GridExportMixin
    ],
    props: {
        initMode: { type: Object, default: () => ({ value: 'compact', icon: 'fal fa-align-justify' }) },
        initFilterModel: { type: Object, default: null },
        initSortModel: { type: Array, default: () => [] },
        initPaginator: { type: Object, default: null },
        initTabId: { type: Number, default: 0 }
    },
    data() {
        return {
            caseOperation: CaseOperation,
            requestOperation: RequestOperation,
            requestTrackingResource: requestTrackingResource,
            REQUEST_STATES: REQUEST_STATES,
            delayDoubleClick: 200,
            preventClick: false,
            tabIndex: 0,
            hideBtn: false,
            url: '/clinical/laboratories/requests/myRequests',
            validationRegex: [],
            busy: false,
            patientSearchFromForm: null,
            sessionState: sessionStore.state,
            sideBarState: sideBarStore.state,
            tabId: this.initTabId,
            pageSize: this.initPaginator ? this.initPaginator.size : 20,
            selectedStudyRequest: null,
            showSidePanel: false,
            columnDefs: clinicalGridColumnDefs(params => this.onIdCellClicked(params)),
            gridApi: null,
            columnApi: null,
            studyRequestDatasource: new ClinicalDatasource(this.url),
            mode: this.initMode,
            options: [
                { value: 'compact', icon: 'fal fa-align-justify' },
                { value: 'normal', icon: 'fal fa-bars' }
            ],
            reqCasId: null,
            nameModal: null,
            storeNameModal: 'clinicalModalsState',
            uiComponentModal: 'uicomponent.petitioner.modalconfig',
            uiComponentGrid: 'uicomponent.petitioner.requestgrid',
            modalConfirmationMessage: '',
            modalConfirmationCheckMessage: '',
            selectedRows: [],
            isPatient: false,
            entitiesIdArray: []
        }
    },
    computed: {
        identifierScopesForSearch() {
            return [
                { scopes: [IDENTIFIER_SCOPES.CENTER_PATIENT, IDENTIFIER_SCOPES.PERSON], icon: 'fal fa-user-circle', text: this.$t('component.searcher.patient'), id: PATIENT_IDENTIFIER },
                { scopes: [IDENTIFIER_SCOPES.REQUEST], icon: 'fal fa-file-medical', text: this.$tc('global.request', 1), errorMessage: this.$t('clinical.error.validIdentifierRequest') },
                { scopes: [IDENTIFIER_SCOPES.CASE], icon: 'fal fa-chart-network', text: this.$tc('global.case', 1), errorMessage: this.$t('clinical.error.validIdentifierCase') }
            ]
        },
        ATTACHMENT_ENTITY_TYPE() {
            return ATTACHMENT_ENTITY_TYPE.REQUEST
        },
        msgSelectedRows() {
            if (this.selectedRows.length) {
                const choice = this.selectedRows.length > 1 ? 2 : 1
                return `${this.selectedRows.length} ${this.$tc('global.selectedElements', choice)}`
            }
            return ''
        },
        regex() {
            return this.$store.getters.regex
        },
        selectedSidebarId() {
            if (!this.sideBarState.selectedOption) {
                return ''
            }
            return this.sideBarState.selectedOption.id
        },
        canCreate() {
            return this.sessionState.flatOperationCodes.includes('operation.entity.request.create')
        },
        canSearchPatients() {
            return this.sessionState.flatOperationCodes.includes('operation.entity.patient.search')
        },
        sideBarFilterModel() {
            if (!this.sideBarState.selectedOption) {
                return {}
            }
            return this.sideBarState.selectedOption.filterModel || this.sideBarState.selectedOption.externalFilterModel || this.sideBarState.selectedOption.url
        },
        sideBar() { // changes when the sideBar is expanded or contracted
            return this.sideBarState.sideBar
        },
        currentTab() {
            return find(this.tabs, tab => tab.id === this.tabId)
        },
        tabs() {
            const tabs = [
                { id: 0, title: 'studyRequest.index.mine', url: '/clinical/laboratories/requests/myRequests' }
            ]
            if (this.sessionState.flatOperationCodes.includes('operation.entity.request.readdepartment')) {
                tabs.push({
                    id: 1,
                    title: 'studyRequest.index.service',
                    url: '/clinical/laboratories/requests/departmentRequests'
                })
            }
            if (this.sessionState.flatOperationCodes.includes('operation.entity.request.readcenter')) {
                tabs.push({
                    id: 2,
                    title: 'studyRequest.index.center',
                    url: '/clinical/laboratories/requests/centerRequests'
                })
            }
            return tabs
        },
        rowHeight() {
            if (this.mode.value === 'compact') {
                return 25
            }
            return 40
        },
        gridOptions() {
            return {
                onModelUpdated: this.onModelUpdated,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                onRowDoubleClicked: this.onRowDoubleClicked,
                getContextMenuItems: this.getContextMenuItems,
                paginationAutoPageSize: false,
                animateRows: true,
                paginationPageSize: this.pageSize,
                getRowHeight: () => this.rowHeight,
                onSortChanged: ({ api }) => api.deselectAll(),
                onFilterChanged: ({ api }) => api.deselectAll(),
                sideBar: gridSideBarConfig
            }
        },
        printEnabled() {
            return this.$_hasLabOperation(this.selectedStudyRequest.laboratory.labId, 'operation.entity.request.printrequestandlabels')
        },
        displayedColumns() {
            return this.columnApi.getAllDisplayedColumns()
        },
        htmlDisabledSelectedRequestCancelButton() {
            return !this.selectedStudyRequest || !this.selectedStudyRequest.metadata.operationGuards.canBeCancelled
        },
        htmlDisabledSelectedRequestDeleteButton() {
            return !this.selectedStudyRequest || !this.selectedStudyRequest.metadata.operationGuards.canBeDeleted
        },
        componentId() {
            return this.sideBarState?.selectedOption?.id?.toString()
        }
    },
    watch: {
        sideBarFilterModel() {
            this.tabIndex = 0
        },
        sideBar() {
            if (this.gridApi) {
                this.gridApi.sizeColumnsToFit()
            }
        }
    },
    created() {
        if (this.$route.params.sidebarOption) {
            const option = sideBarStore.findOptionById(this.$route.params.sidebarOption)
            sideBarStore.setSelectedOption(option)
        }
    },
    async mounted() {
        this.validationRegex = this.regex.filter(reg => reg.identifierType.idtScope === IDENTIFIER_SCOPES.REQUEST)
        if (this.$route.params.patientSearch) {
            if (this.gridApi) {
                await this.patientSearch(this.$route.params.patientSearch)
            } else {
                this.patientSearchFromForm = this.$route.params.patientSearch
            }
        }
    },
    beforeDestroy() {
        this.gridApi?.removeEventListener(AgGridCallbackEventType.SUCCESS, this.onServerSuccessCallback)
    },
    methods: {
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onRowDoubleClicked({ data }) {
            this.preventClick = true
            this.$router.push({
                name: 'editRequest',
                params: {
                    requestId: data.id,
                    labId: data.laboratory.labId
                }
            })
        },
        async loadModalConfiguration() {
            const customConfig = await customConfigResource.get(this.modalUiComponent)
            if (customConfig) {
                await this.$_setModalConfirmationState('clinicalModalsState', customConfig.config)
            }
        },
        clearSearch() {
            sideBarStore.selectFallbackOption()
        },
        async requestSearch(search) {
            this.updateLoading(true)
            const [err, response] = await this.toCall(clinicalRequestService.searchRequestByLabel(search))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal(err.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND ? this.$t('component.searcher.error.noRequest') : this.$t('component.modalError.title'))
                return
            }
            this.$router.push({
                name: 'editRequest',
                params: {
                    requestId: response.requestId,
                    labId: response.laboratoryId
                }
            })
        },
        async patientSearch(searchInformation) {
            const params = {
                size: PAGINATOR_BIG_PAGE_SIZE,
                id: searchInformation.identifierSearch,
                surname1: searchInformation.patientSurname1Search,
                surname2: searchInformation.patientSurmname2Search,
                middleName: searchInformation.patientMiddlenameSearch,
                name: searchInformation.patientNameSearch,
                centerCode: searchInformation.center,
                identifierType: searchInformation.type
            }
            this.updateLoading(true)
            const [err, response] = await this.toCall(PatientResource.paginator(params))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal(err.response.data.code === this.$apiExceptions.RESULT_LIMIT_EXCEEDED
                    ? this.$t('component.patient.searcher.tooManySearchResults') : this.$t('component.modalError.title'))
                return
            }
            const patients = response.content
            if (!patients.length && searchInformation.identifierSearch) {
                this.showErrorModalGlobal(this.$t('component.patient.searcher.noResultsWithIdentifier', { identifier: searchInformation.identifierName }))
            } else if (!patients.length) {
                this.showErrorModalGlobal(this.$t('component.patient.searcher.noResults'))
            } else if (patients.length === 1) {
                this.$router.push({ name: 'clinicalPatientDetail', params: { id: patients[0].patId } })
            } else {
                this.$router.push({ name: 'clinicalPatientGrid', params: { patients: patients } })
            }
        },
        async studySearch(searchInformation) {
            this.updateLoading(true)
            const [err, response] = await this.toCall(clinicalRequestService.searchRequestByCaseNumber(searchInformation))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal(err.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND ? this.$t('component.searcher.error.noCase') : this.$t('component.modalError.title'))
                return
            }
            this.$router.push({
                name: 'editRequest',
                params: {
                    requestId: response.requestId,
                    labId: response.laboratoryId
                }
            })
        },
        onModelUpdated({ api }) {
            api.getColumnDef('status').suppressMenu = this.selectedSidebarId === 'draftRequests'
            api.refreshHeader()
        },
        applySideBarFilterModel() {
            if (this.gridApi) {
                if (this.sideBarState.selectedOption.url) {
                    this.url = this.sideBarState.selectedOption.url
                } else {
                    this.url = '/clinical/laboratories/requests/myRequests'
                }

                if (this.sideBarState.selectedOption.externalFilterModel) {
                    if (this.sideBarState.selectedOption.filterModel) {
                        this.gridApi.setFilterModel(this.sideBarState.selectedOption.filterModel)
                    } else {
                        this.gridApi.setFilterModel(null)
                    }
                } else {
                    this.gridApi.setFilterModel(this.sideBarFilterModel)
                }
                this.showSidePanel = false
                this.gridApi.deselectAll()
            }
        },
        onServerSuccessCallback() {
            if (this.initPaginator) {
                this.gridApi.paginationGoToPage(this.initPaginator.number)
            }
        },
        onGridReady(event) {
            this.gridApi = event.api
            this.columnApi = event.columnApi

            this.gridApi.setColumnDefs(
                this.$_filterPatientColumns(
                    this.columnDefs,
                    'patient.middleName',
                    'patient.surname2'))

            if (this.initFilterModel) {
                this.gridApi.setFilterModel(this.initFilterModel)
            } else if (this.sideBarFilterModel) {
                this.applySideBarFilterModel()
            }

            if (this.patientSearchFromForm) {
                this.patientSearch(this.patientSearchFromForm)
            }

            this.gridApi.addEventListener(AgGridCallbackEventType.SUCCESS, this.onServerSuccessCallback)
            this.studyRequestDatasource = new ClinicalDatasource(this.url, this.sideBarState.selectedOption.externalFilterModel)
            this.gridApi.setServerSideDatasource(this.studyRequestDatasource)
        },
        exportReport(exportType) {
            const columns = this.$_getColumnsToExport(this.gridApi, this.columnApi)
            if (columns && this.gridApi) {
                return clinicalRequestPrintService.exportTo(
                    this.url,
                    this.$tc('global.clinic'),
                    this.gridApi.getFilterModel(),
                    GridUtils.getSortValue(this.gridApi.getSortModel()),
                    columns,
                    this.selectedRows.map(selectedRow => selectedRow.id),
                    exportType)
            }
        },
        onSideBarClickAddFilter() {
            this.$refs.addFilterModal.show()
        },
        async onSideBarClickUpdateFilter(option) {
            if (isEmpty(this.gridApi.getFilterModel())) {
                this.saveNoFilter()
                return
            }
            const [err] = await this.toCall(sideBarStore.updateCustomOption(option, { filterModel: this.gridApi.getFilterModel(), url: this.sideBarState.selectedOption.url, externalFilterModel: this.sideBarState.selectedOption.externalFilterModel }))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            await sideBarStore.loadCustomOptions()
            this.showToastSuccessGlobal(this.$t('global.filterUpdated'))
        },
        async onAddFilterOk(filter) {
            if (isEmpty(this.gridApi.getFilterModel())) {
                this.saveNoFilter()
                return
            }
            const params = { label: filter.label, filterModel: this.gridApi.getFilterModel(), url: this.sideBarState.selectedOption.url, externalFilterModel: this.sideBarState.selectedOption.externalFilterModel }
            await customFilterResource.create(this.sideBarState.componentCode, params)
            await sideBarStore.loadCustomOptions()
            this.showToastSuccessGlobal(this.$t('global.filterAdded'))
        },
        async onAttachmentsAdded() {
            this.gridApi.setServerSideDatasource(this.studyRequestDatasource)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            GridUtils.changeRowHeightGrid(this.gridApi, selectedOption.size)
        },
        reloadRowData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(this.studyRequestDatasource)
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        restoreColumns() {
            this.gridApi.resetColumnState()
        },
        cleanFilters() {
            this.applySideBarFilterModel()
        },
        onIdCellClicked({ data: studyRequest }) {
            setTimeout(async () => {
                if (!this.preventClick) {
                    this.reqCasId = studyRequest && studyRequest.caseStudy && studyRequest.caseStudy.casId
                    const isSame = this.selectedStudyRequest && studyRequest.id === this.selectedStudyRequest.id
                    if (isSame) {
                        this.showSidePanel = !this.showSidePanel
                        return
                    }
                    this.updateLoading(true)
                    const [err, response] = await this.toCall(commonRequestService.getRequestByIdForSidePanel(studyRequest.laboratory.labId, studyRequest.id))
                    this.updateLoading(false)
                    if (err) {
                        this.showErrorModalGlobal()
                        return
                    }
                    this.selectedStudyRequest = null
                    this.$nextTick(() => {
                        this.selectedStudyRequest = response
                    })
                    this.showSidePanel = true
                }
            }, this.delayDoubleClick)
        },

        /**
         * PRINT METHODS
         */
        async downloadRequestReportPdf() {
            const blob =
                await this.$_toCallSecure(
                    clinicalRequestPrintService.downloadRequestReportPdf(
                        [this.selectedStudyRequest.id]))
            if (blob) {
                const openFileParams = {
                    blob,
                    newWindowTabTitle: `${this.selectedStudyRequest.label} - ${this.$tc('global.report')}`
                }
                this.$_openFile(openFileParams)
            }
        },
        async downloadCaseAPReportPdf() {
            const getCaseReportPDFParams = {
                laboratoryId: this.selectedStudyRequest.laboratory.labId,
                caseId: this.reqCasId,
                caseLabel: this.selectedStudyRequest.caseLabel,
                print: false,
                hasAssociatedCases: this.selectedStudyRequest.hasCaseStudiesAssociated,
                isClinical: true
            }
            await HelperUtils.getCaseReportPDF(getCaseReportPDFParams)
            this.$emit('loadSummary')
        },
        printSelectedSubrequestLabelsWithCopies(elementsWithCopies) {
            this.$refs.printComponent.printSubrequestReportFromClinical(this.selectedStudyRequest.laboratory.labId, this.selectedStudyRequest.id, elementsWithCopies)
        }
    }
}

</script>

<style lang="scss">
    .study-request-index .content-scroll {
        height: calc(100vh - 140px);
        overflow-y: auto;
        overflow-x: hidden;
    }
</style>

<style scoped lang="scss">

    .custom-print {
        margin-top: 1.2rem !important;
    }

    .custom-print .inner-icon {
        font-size: 3rem !important;
    }

    .simple-pop-inside {
        width: 150px !important;
    }

    .study-request-grid {
        height: 100%;
    }

    .study-request-patient-grid {
        height: 100%;
    }

    .multiselect__option {
        text-align: center !important;
    }

    .sr-panel-head {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
        padding-left: 24px;
        padding-right: 24px;
    }

    .sr-panel-mid-section {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        margin-bottom: 10px;
    }

    .sr-panel-nav {
        .nav-link {
            color: $base-text-color;
            border-bottom: 1px solid #d8d8d8;

            &.active {
                color: $header-text-color;
                border-bottom: 1px solid black;
            }

            &:hover {
                color: $header-text-color;
            }
        }
    }

    .sr-panel-filters {
        display: flex;
        align-items: center;
        flex-grow: 1;
        padding-right: 24px;
        padding-bottom: 5px;
        border-bottom: 1px solid #d8d8d8;

        & > :not(:last-child) {
            margin-right: 45px;
        }

        .sr-panel-end {
            margin-left: auto;
        }
    }

    .filter-label {
        margin-right: .5rem;
        font-size: font-size(small);
    }

    @media (max-width: 1300px) {
        .study-request-index .vt-side-panel {
            width: 60%;
        }
    }

</style>
